import { CompanionAd, ResourceType } from '../model/CompanionAd';
import {
  Creative,
  CreativeLinear,
  InlineAd,
  MediaFile,
  TrackingEvent,
  VASTAd,
  VideoClick,
  WrapperAd,
} from '../model/VASTAdTypes';
import { XMLParser } from './XmlParser';
import { VastElement } from '../model/VASTElement';

export class VASTParser {
  private vastAd: VASTAd;
  constructor() {
    this.vastAd = {
      id: null,
    };
  }

  public parse(xml: HTMLElement): VASTAd | undefined {
    const adXML: HTMLCollection = xml.getElementsByTagName(VastElement.AD);
    if (adXML[0] === undefined) {
      return this.vastAd;
    }

    const id: string | null = adXML[0].getAttribute(VastElement.ID);
    this.vastAd = {
      id: id,
    };
    this.parseAdTag(adXML);
    return this.vastAd;
  }

  private trim(value: any): any {
    if (value === undefined) return null;

    return value.replace(/^\s+|\s+$/g, '');
  }

  private parseAdTag(xml: any): any {
    const xmlLength = xml.length;
    for (let i = 0; i < xmlLength; i++) {
      if (xml[i].getElementsByTagName(VastElement.INLINE).length > 0) {
        this.vastAd.inlineAd = this.parseInLineTag(xml[i].getElementsByTagName(VastElement.INLINE));
      } else if (xml[i].getElementsByTagName(VastElement.WRAPPER).length > 0) {
        this.parseWrapperTag(xml[i].getElementsByTagName(VastElement.WRAPPER));
      } else {
        console.error('VAST - parseAdTag() - Unsupported VAST tag');
      }
    }
  }

  private parseInLineTag(xml: any): InlineAd {
    const vastInlineAd: InlineAd = {};
    const nodeArray: any = xml[0].childNodes;
    const nodeArrayLength = nodeArray.length;
    for (let i = 0; i < nodeArrayLength; i++) {
      if (nodeArray[i].nodeName === VastElement.AD_SYSTEM) {
        vastInlineAd.adSystem = XMLParser.textContent(nodeArray[i]);
      } else if (nodeArray[i].nodeName === VastElement.AD_TITLE) {
        vastInlineAd.adTitle = XMLParser.textContent(nodeArray[i]);
      } else if (nodeArray[i].nodeName === VastElement.ERROR) {
        vastInlineAd.errorURL = this.trim(XMLParser.textContent(nodeArray[i]));
      } else if (nodeArray[i].nodeName === VastElement.DESCRIPTION) {
        vastInlineAd.description = XMLParser.textContent(nodeArray[i]);
      }
    }

    vastInlineAd.impressions = this.parseImpressionTag(xml[0].getElementsByTagName(VastElement.IMPRESSION));
    vastInlineAd.creatives = this.parseCreatives(xml[0].getElementsByTagName(VastElement.CREATIVES));
    return vastInlineAd;
  }

  private parseWrapperTag(xml: any): WrapperAd {
    const vastWrapperAd: WrapperAd = {};
    const nodeArray = xml[0].childNodes;
    for (let i = 0; i < nodeArray.length; i++) {
      if (nodeArray[i].nodeName === VastElement.VAST_AD_TAG_URI) {
        vastWrapperAd.vastAdTagURL = this.trim(XMLParser.textContent(nodeArray[i]));
      } else if (nodeArray[i].nodeName === VastElement.ERROR) {
        vastWrapperAd.error = this.trim(XMLParser.textContent(nodeArray[i]));
      }
    }
    vastWrapperAd.impressions = this.parseImpressionTag(xml[0].getElementsByTagName(VastElement.IMPRESSION));
    vastWrapperAd.creatives = this.parseCreatives(xml[0].getElementsByTagName(VastElement.CREATIVES));
    return vastWrapperAd;
  }

  private parseImpressionTag(xml: any): any {
    const impressionsArray: string[] = [];

    if (xml === undefined) return impressionsArray;

    const impressionsArrayLength = xml.length;
    for (let i = 0; i < impressionsArrayLength; i++) {
      impressionsArray.push(this.trim(XMLParser.textContent(xml[i])));
    }

    return impressionsArray;
  }

  private parseCreatives(xml: any): Creative[] {
    const creatives: Creative[] = [];
    if (xml[0] === undefined || xml[0].getElementsByTagName(VastElement.CREATIVE) === undefined) {
      return [];
    }

    const creativesArrayLength = xml[0].getElementsByTagName(VastElement.CREATIVE).length;
    const creativeArray = xml[0].getElementsByTagName(VastElement.CREATIVE);

    for (let i = 0; i < creativesArrayLength; i++) {
      creatives.push(this.parseCreative(creativeArray[i]));
    }

    return creatives;
  }

  private parseCreative(xml: any): any {
    const creative: Creative = {};
    if (xml.getElementsByTagName(VastElement.LINEAR).length != 0) {
      creative.linearElement = this.parseCreativeLinear(xml.getElementsByTagName(VastElement.LINEAR));
    } else if (xml.getElementsByTagName(VastElement.COMPANION_ADS).length != 0) {
      creative.companionAds = this.parseCreativeCompanionAds(xml.getElementsByTagName(VastElement.COMPANION_ADS));
    }
    return creative;
  }

  private parseCreativeLinear(xml: any): any {
    const creativeLinear: CreativeLinear = {};
    const nodeArray = xml[0].childNodes;
    const nodeArrayLength = nodeArray.length;
    for (let i = 0; i < nodeArrayLength; i++) {
      if (nodeArray[i].nodeName === VastElement.DURATION) {
        creativeLinear.videoDuration = XMLParser.textContent(nodeArray[i]);
      } else if (nodeArray[i].nodeName === VastElement.MEDIA_FILES) {
        creativeLinear.mediaFiles = this.parseMediaFiles(nodeArray[i]);
      } else if (nodeArray[i].nodeName === VastElement.VIDEO_CLICKS) {
        creativeLinear.videoClick = this.parseVideoClick(nodeArray[i]);
      } else if (nodeArray[i].nodeName === VastElement.TRACKING_EVENTS) {
        creativeLinear.trackingEvents = this.parseTrackingEvents(nodeArray[i]);
      }
    }
    return creativeLinear;
  }

  private parseTrackingEvents(xml: any): any {
    const trackingEvents: TrackingEvent[] = [];
    const trackings = xml.getElementsByTagName(VastElement.TRACKING);
    for (let i = 0; i < trackings.length; i++) {
      const trackingEvent: TrackingEvent = {};
      trackingEvent.type = trackings[i].getAttribute(VastElement.EVENT);
      trackingEvent.urls?.push(XMLParser.textContent(trackings[i]));
      trackingEvents.push(trackingEvent);
    }
    return trackingEvents;
  }

  private parseVideoClick(xml: any): VideoClick {
    const videoClick: VideoClick = {
      clickTrackings: [],
    };
    const nodeArray = xml.childNodes;
    for (let i = 0; i < nodeArray.length; i++) {
      if (nodeArray[i].nodeName === VastElement.CLICK_THROUGH) {
        videoClick.clickThrough = this.trim(XMLParser.textContent(nodeArray[i]));
      } else if (nodeArray[i].nodeName === VastElement.CLICK_TRACKING) {
        videoClick.clickTrackings.push(this.trim(XMLParser.textContent(nodeArray[i])));
      }
    }
    return videoClick;
  }

  private parseMediaFiles(xml: any): any {
    const vastMediaFiles: MediaFile[] = [];
    const mediaFiles = xml.getElementsByTagName(VastElement.MEDIA_FILE);
    for (let i = 0; i < mediaFiles.length; i++) {
      const mediaFile: MediaFile = {};
      mediaFile.id = mediaFiles[i].getAttribute(VastElement.ID);
      mediaFile.delivery = mediaFiles[i].getAttribute(VastElement.DELIVERY);
      mediaFile.bitrate = mediaFiles[i].getAttribute(VastElement.BITRATE);
      mediaFile.width = Number(mediaFiles[i].getAttribute(VastElement.WIDTH));
      mediaFile.height = Number(mediaFiles[i].getAttribute(VastElement.HEIGHT));
      mediaFile.scalable = mediaFiles[i].getAttribute(VastElement.SCALABLE) === 'true' ? true : false;
      mediaFile.maintainAspectRatio =
        mediaFiles[i].getAttribute(VastElement.MAINTAIN_ASPECT_RATIO) === 'true' ? true : false;
      mediaFile.mediaUrl = this.trim(XMLParser.textContent(mediaFiles[i]));
      vastMediaFiles.push(mediaFile);
    }
    return vastMediaFiles;
  }

  private parseCreativeCompanionAds(xml: any): any {
    const companionAds: CompanionAd[] = [];
    const companions: any = xml[0].getElementsByTagName(VastElement.COMPANION);
    const companionsLength: any = companions.length;
    for (let i = 0; i < companionsLength; i++) {
      const companionAd: CompanionAd = {
        id: '',
        creativeView: [],
        height: 0,
        width: 0,
      };
      companionAd.id = companions[i].getAttribute(VastElement.ID);
      companionAd.width = Number(companions[i].getAttribute(VastElement.WIDTH));
      companionAd.height = Number(companions[i].getAttribute(VastElement.HEIGHT));
      companionAd.expandedWidth = Number(companions[i].getAttribute(VastElement.EXPANDED_WIDTH));
      companionAd.expandedHeight = Number(companions[i].getAttribute(VastElement.EXPANDED_HEIGHT));

      const nodeArray = companions[i].childNodes;
      this.parseCompanionAdsChildNodes(nodeArray, companionAd);

      companionAds.push(companionAd);
    }
    return companionAds;
  }

  private parseCompanionAdsChildNodes(nodeArray: any, companionAd: CompanionAd): void {
    const nodeArrayLength = nodeArray.length;
    for (let j = 0; j < nodeArrayLength; j++) {
      if (nodeArray[j].nodeName === VastElement.AD_PARAMETERS) {
        companionAd.adParameters = XMLParser.textContent(nodeArray[j]);
      } else if (nodeArray[j].nodeName === VastElement.ALT_TEXT) {
        companionAd.altText = XMLParser.textContent(nodeArray[j]);
      } else if (nodeArray[j].nodeName === VastElement.COMPANION_CLICK_THROUGH) {
        companionAd.clickThroughURL = this.trim(XMLParser.textContent(nodeArray[j]));
      } else if (nodeArray[j].nodeName === VastElement.IFRAME_RESOURCE) {
        companionAd.resourceType = ResourceType.IFRAME;
        companionAd.url = this.trim(XMLParser.textContent(nodeArray[j]));
      } else if (nodeArray[j].nodeName === VastElement.HTML_RESOURCE) {
        companionAd.resourceType = ResourceType.HTML;
        companionAd.code = XMLParser.textContent(nodeArray[j]);
      } else if (nodeArray[j].nodeName === VastElement.STATIC_RESOURCE) {
        companionAd.resourceType = ResourceType.STATIC;
        companionAd.creativeType = nodeArray[j].getAttribute(VastElement.CREATIVE_TYPE);
        companionAd.url = this.trim(XMLParser.textContent(nodeArray[j]));
      } else if (nodeArray[j].nodeName === VastElement.TRACKING_EVENTS) {
        const trackings = nodeArray[j].getElementsByTagName(VastElement.TRACKING);
        this.parseCreativeView(trackings, companionAd);
      } else if (nodeArray[j].nodeName === VastElement.COMPANION_CLICK_TRACKING) {
        companionAd.clickTrackingURL = this.trim(XMLParser.textContent(nodeArray[j]));
      }
    }
  }

  private parseCreativeView(trackings: any, companionAd: CompanionAd): any {
    const trackingsLength = trackings.length;
    if (trackingsLength > 0) {
      companionAd.creativeView = [];
      for (let k = 0; k < trackingsLength; k++) {
        const type = trackings[k].getAttribute(VastElement.EVENT);
        if (type === VastElement.CREATIVE_VIEW) {
          companionAd.creativeView.push(this.trim(XMLParser.textContent(trackings[k])));
        }
      }
    }
  }
}
